import { useSelector } from 'react-redux';
import { useGetDatasetsQuery } from '../state/apiSlice';
import { isAdmin } from './datasetUI';

/**
 * Centralised rule for whether the Admin/MQTT tab should be visible to
 * the user, plus what mode the page should render in.
 *
 * Two visibility modes:
 *   - 'admin' — the user has admin/owner role on at least one dataset,
 *               OR has the create-dataset capability. They see the full
 *               UI: Create button, action buttons on each row, MQTT
 *               setup dialog, delete dialog. Tab labelled "Admin".
 *   - 'user'  — the user is logged in and has at least one dataset they
 *               can read (own role !== 'none' OR dataset is open). They
 *               see a read-only version of the same page: no Create
 *               button, no row actions besides Open in Overview and the
 *               read-only Refresh. Tab labelled "My MQTT".
 *               This is a deliberate over-approximation of "has any MQTT
 *               setup" — fanning out N status calls to gate the tab
 *               itself would be too expensive. Datasets without an MQTT
 *               setup still appear (their row says "MQTT: not set up");
 *               the body is honest about what's there.
 *
 * canCreateDataset is read from the Redux session slice (populated at
 * login by POST /v1/login or on boot by GET /v1/validatesession). No
 * /v1/me round-trip — see AGENTS.md and the loginSlice docstring.
 *
 * Returns:
 *   { visible, mode, label, canCreateDataset, adminDatasets, isLoading }
 *
 *   visible: bool — convenience for `mode != null`
 *   mode:    'admin' | 'user' | null (null when hidden)
 *   label:   'Admin' | 'My MQTT' | null — for the tab strip
 *   canCreateDataset: bool — hint, not authoritative (server enforces)
 *   adminDatasets:    array of datasets the user owns/admins
 *   isLoading: bool — datasets query in flight
 */
export function useAdminVisibility() {
    const canCreateDataset = useSelector(
        (state) => !!state.login.user?.canCreateDataset
    );
    const loggedIn = useSelector((state) => !!state.login.value);
    const { data: datasets, isLoading: dsLoading } = useGetDatasetsQuery();

    const adminDatasets = datasets?.raw?.filter(isAdmin) || [];
    // datasets.filtered is the catalog's "datasets I can read" view —
    // computed once in the apiSlice transformResponse so we don't redo
    // the AccessRole/AccessType filter here. Falls back to [] before
    // the query resolves.
    const accessibleDatasets = datasets?.filtered || [];

    const isAdminMode = canCreateDataset || adminDatasets.length > 0;
    const isUserMode = !isAdminMode && loggedIn && accessibleDatasets.length > 0;

    let mode = null;
    let label = null;
    if (isAdminMode) {
        mode = 'admin';
        label = 'Admin';
    } else if (isUserMode) {
        mode = 'user';
        label = 'My MQTT';
    }

    return {
        visible: mode != null,
        mode,
        label,
        canCreateDataset,
        adminDatasets,
        accessibleDatasets,
        isLoading: dsLoading
    };
}
